/**
 * dLocal Smart Fields Style Mapper
 * 
 * Converts Altruon styleCustomization object to dLocal Smart Fields style format.
 * 
 * dLocal Smart Fields accepts styles in this structure:
 * {
 *   base: { ... },      // Default state
 *   invalid: { ... },   // Error state
 *   complete: { ... }   // Valid state
 * }
 * 
 * @see https://docs.dlocal.com/docs/overview-smart-fields
 */

/**
 * Maps Altruon style object to dLocal Smart Fields style format
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object from StyleContext
 * @returns {Object} dLocal Smart Fields compatible style object
 */
export const mapStylesToSmartFields = (altruonStyles) => {
  if (!altruonStyles) return getDefaultSmartFieldsStyles();

  const {
    components = {},
    colors = {},
    typography = {},
  } = altruonStyles;

  const input = components.input || {};

  // Ensure we always have a visible text color (never transparent or empty)
  const textColor = input.textColor || colors.textPrimary || '#111827';
  const finalTextColor = textColor && textColor !== 'transparent' && textColor !== 'rgba(0,0,0,0)' 
    ? textColor 
    : '#111827';

  return {
    base: {
      color: finalTextColor,
      fontSize: input.fontSize || typography.fontSizes?.md || '16px',
      fontFamily: typography.fontFamily || 'Arial, sans-serif',
      fontWeight: input.fontWeight || typography.fontWeights?.normal || '400',
      lineHeight: typography.lineHeight?.normal || '1.5',
      letterSpacing: typography.letterSpacing?.normal || 'normal',
      '::placeholder': {
        color: input.placeholderColor || '#9CA3AF',
      },
    },
    invalid: {
      color: colors.error || components.errorMessage?.color || '#EF4444',
      iconColor: colors.error || '#EF4444',
    },
    complete: {
      color: colors.success || colors.primary || '#10B981',
    },
  };
};

/**
 * Returns default Smart Fields styles (fallback)
 * 
 * @returns {Object} Default dLocal Smart Fields style object
 */
const getDefaultSmartFieldsStyles = () => ({
  base: {
    color: '#000000',  // Pure black for maximum visibility
    fontSize: '16px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    lineHeight: '1.5',
    letterSpacing: 'normal',
    '::placeholder': {
      color: '#999999',  // Medium gray for placeholders
    },
  },
  invalid: {
    color: '#EF4444',
    iconColor: '#EF4444',
  },
  complete: {
    color: '#10B981',
  },
});

/**
 * Determines country code from currency code
 * Used for Smart Fields initialization
 * 
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'INR', 'BRL')
 * @returns {string} ISO 3166-1 alpha-2 country code (e.g., 'IN', 'BR')
 */
export const getCountryFromCurrency = (currencyCode) => {
  const currencyToCountry = {
    'INR': 'IN', // India
    'BRL': 'BR', // Brazil
    'MXN': 'MX', // Mexico
    'ARS': 'AR', // Argentina
    'CLP': 'CL', // Chile
    'COP': 'CO', // Colombia
    'PEN': 'PE', // Peru
    'UYU': 'UY', // Uruguay
    'PYG': 'PY', // Paraguay
    'BOB': 'BO', // Bolivia
    'USD': 'US', // United States
    'EUR': 'ES', // Spain (default for EUR)
    'GBP': 'GB', // United Kingdom
    'CAD': 'CA', // Canada
    'AUD': 'AU', // Australia
    'JPY': 'JP', // Japan
    'CNY': 'CN', // China
    'KRW': 'KR', // South Korea
    'SGD': 'SG', // Singapore
    'HKD': 'HK', // Hong Kong
    'THB': 'TH', // Thailand
    'MYR': 'MY', // Malaysia
    'IDR': 'ID', // Indonesia
    'PHP': 'PH', // Philippines
    'VND': 'VN', // Vietnam
    'PKR': 'PK', // Pakistan
    'BDT': 'BD', // Bangladesh
    'EGP': 'EG', // Egypt
    'NGN': 'NG', // Nigeria
    'KES': 'KE', // Kenya
    'GHS': 'GH', // Ghana
    'ZAR': 'ZA', // South Africa
    'MAD': 'MA', // Morocco
    'TRY': 'TR', // Turkey
    'SAR': 'SA', // Saudi Arabia
    'AED': 'AE', // United Arab Emirates
    'QAR': 'QA', // Qatar
    'OMR': 'OM', // Oman
    'BHD': 'BH', // Bahrain
    'JOD': 'JO', // Jordan
  };

  return currencyToCountry[currencyCode?.toUpperCase()] || 'US';
};

/**
 * Maps currency code to locale for Smart Fields
 * Used for SDK initialization (determines labels, formatting)
 * 
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Locale code (e.g., 'en', 'pt', 'es')
 */
export const getLocaleFromCurrency = (currencyCode) => {
  const currencyToLocale = {
    'INR': 'en', // English for India
    'BRL': 'pt', // Portuguese for Brazil
    'MXN': 'es', // Spanish for Mexico
    'ARS': 'es', // Spanish for Argentina
    'CLP': 'es', // Spanish for Chile
    'COP': 'es', // Spanish for Colombia
    'PEN': 'es', // Spanish for Peru
    'UYU': 'es', // Spanish for Uruguay
    'PYG': 'es', // Spanish for Paraguay
    'BOB': 'es', // Spanish for Bolivia
    'USD': 'en', // English for US
    'EUR': 'en', // English default for EUR
    'GBP': 'en', // English for UK
    'CAD': 'en', // English for Canada
    'AUD': 'en', // English for Australia
    'JPY': 'ja', // Japanese for Japan
    'CNY': 'zh', // Chinese for China
    'KRW': 'ko', // Korean for South Korea
    'THB': 'th', // Thai for Thailand
    'VND': 'vi', // Vietnamese for Vietnam
    'PHP': 'en', // English for Philippines
    'MYR': 'en', // English for Malaysia
    'IDR': 'id', // Indonesian for Indonesia
    'SGD': 'en', // English for Singapore
    'HKD': 'zh', // Chinese for Hong Kong
    'PKR': 'en', // English for Pakistan
    'BDT': 'bn', // Bengali for Bangladesh
    'EGP': 'ar', // Arabic for Egypt
    'NGN': 'en', // English for Nigeria
    'KES': 'en', // English for Kenya
    'GHS': 'en', // English for Ghana
    'ZAR': 'en', // English for South Africa
    'MAD': 'ar', // Arabic for Morocco
    'TRY': 'tr', // Turkish for Turkey
    'SAR': 'ar', // Arabic for Saudi Arabia
    'AED': 'ar', // Arabic for UAE
    'QAR': 'ar', // Arabic for Qatar
    'OMR': 'ar', // Arabic for Oman
    'BHD': 'ar', // Arabic for Bahrain
    'JOD': 'ar', // Arabic for Jordan
  };

  return currencyToLocale[currencyCode?.toUpperCase()] || 'en';
};

/**
 * Maps card brand codes to display names
 * 
 * @param {string} brandCode - dLocal card brand code (e.g., 'VI', 'MC')
 * @returns {string} Display name (e.g., 'Visa', 'Mastercard')
 */
export const getCardBrandName = (brandCode) => {
  const brandNames = {
    'VI': 'Visa',
    'MC': 'Mastercard',
    'AE': 'American Express',
    'DC': 'Diners Club',
    'DS': 'Discover',
    'JC': 'JCB',
    'UP': 'UnionPay',
    'EL': 'Elo',
    'HI': 'Hipercard',
    'CA': 'Cabal',
    'NR': 'Naranja',
    'TS': 'Tarjeta Shopping',
    'CS': 'Cencosud',
  };

  return brandNames[brandCode?.toUpperCase()] || 'Card';
};

/**
 * Validates if a country supports card payments via dLocal
 * 
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {boolean} True if country is supported
 */
export const isCountrySupported = (countryCode) => {
  const supportedCountries = [
    'IN', 'BR', 'MX', 'AR', 'CL', 'CO', 'PE', 'UY', 'PY', 'BO',
    'US', 'CA', 'GB', 'ES', 'FR', 'DE', 'IT',
    'CN', 'JP', 'KR', 'SG', 'HK', 'TH', 'MY', 'ID', 'PH', 'VN',
    'PK', 'BD', 'EG', 'NG', 'KE', 'GH', 'ZA', 'MA', 'TR',
    'SA', 'AE', 'QA', 'OM', 'BH', 'JO',
  ];

  return supportedCountries.includes(countryCode?.toUpperCase());
};

